import React, { useState, useEffect } from 'react';
import cn from 'classnames';

import ButtonGroup, { ButtonFields } from '@components/buttongroup';
import { CloseIcon } from '@components/closeicon';
import Typography from '@components/typography';
import { useDeviceDetect } from '@hooks/usedevicedetect';
import useToggleLiveChat from '@hooks/usetogglelivechat';

import '@styles/configure/imagegallery.scss';

type DivProps = React.HTMLAttributes<HTMLDivElement>;

export interface ImageGalleryNavigationBarProps extends DivProps {
  onClose?: Function;
  onMainSelected?: (mainSelected: string) => void;
  onSubSelected?: (subSelected: string) => void;
  mainSelected?: string;
  subSelected?: string;
}

export enum MainButtons {
  ImageGallery = 'Image Gallery',
  View360 = '360 View',
}

export enum SubButtons {
  Exterior = 'Exterior',
  Interior = 'Interior',
}

const view360 = '360 View';
const imageGallery = 'Image Gallery';
const buttonsNormalText =
  'image-gallery-container__navigation-bar__buttons-text';
const button1ActiveTextClassName =
  'image-gallery-container__navigation-bar__buttons1-text--active';

const ImageGalleryNavigationBar: React.FC<ImageGalleryNavigationBarProps> = ({
  onClose,
  onMainSelected,
  onSubSelected,
  mainSelected,
  subSelected,
}) => {
  const { isMobile } = useDeviceDetect();
  const { showElement } = useToggleLiveChat();

  const interior = `Interior${isMobile ? '' : ' View'}`;
  const exterior = `Exterior${isMobile ? '' : ' View'}`;

  // Interior-Exterior button group has different active style on both mobile and desktop
  const subButtonsActiveTextClassName = `image-gallery-container__navigation-bar__subbuttons-${
    isMobile ? 'mobile-' : ''
  }text--active`;
  // Interior-Exterior button group has different normal style on mobile
  const subButtonNormalTextClassName = `image-gallery-container__navigation-bar__subbuttons-${
    isMobile ? 'mobile-' : ''
  }text`;
  const [currentSelection, setCurrenSelection] = useState<string>(
    MainButtons.ImageGallery
  );
  const [currentSubSelection, setSubCurrenSelection] = useState<string>('');

  const selectedClickHandle = (selected: string) => {
    setCurrenSelection(selected);
    if (onMainSelected) {
      onMainSelected(selected);
    }

    if (selected === MainButtons.View360 && !currentSubSelection) {
      setSubCurrenSelection(exterior);
      if (onSubSelected) {
        onSubSelected(exterior);
      }
    }
  };

  useEffect(() => {
    if (mainSelected) {
      selectedClickHandle(mainSelected);
    }
  }, [mainSelected]);

  // Main buttons configuration
  const buttons: ButtonFields[] = [
    {
      Id: MainButtons.ImageGallery,
      Label: imageGallery,
      ActiveTextClassName: button1ActiveTextClassName,
      NormalTextClassName: buttonsNormalText,
    },
    {
      Id: MainButtons.View360,
      Label: view360,
      ActiveTextClassName: !isMobile
        ? 'image-gallery-container__navigation-bar__buttons2-text--active'
        : button1ActiveTextClassName,
      NormalTextClassName: buttonsNormalText,
    },
  ];

  // Exterior Interior buttons configuration
  const subButtons: ButtonFields[] = [
    {
      Id: SubButtons.Exterior,
      Label: exterior,
      ActiveTextClassName: subButtonsActiveTextClassName,
      NormalTextClassName: subButtonNormalTextClassName,
    },
    {
      Id: SubButtons.Interior,
      Label: interior,
      ActiveTextClassName: subButtonsActiveTextClassName,
      NormalTextClassName: subButtonNormalTextClassName,
    },
  ];

  const subSelectedClickHandle = (selected: string) => {
    setSubCurrenSelection(selected);
    if (onSubSelected) {
      onSubSelected(selected);
    }
  };

  // It needs to be rendered differently on mobile
  const renderMobile = () => {
    return (
      <div className="image-gallery-container__navigation-bar__subbuttons">
        <ButtonGroup
          buttons={subButtons}
          activeButton={
            subSelected && subSelected.indexOf('Exterior') > -1 ? 0 : 1
          }
          onClick={(selected) => subSelectedClickHandle(selected)}
          data-cy="image-gallery-container__navigation-bar__subbuttons"
          buttonClassName={cn(
            'button-group__custom-button-sub',
            'image-gallery-container__navigation-bar__subbuttons-mobile'
          )}
          activeButtonClassName={
            'image-gallery-container__navigation-bar__subbuttons-mobile--active'
          }
          dataTestId="image-gallery-container__navigation-bar__subbuttons"
        />
      </div>
    );
  };

  const handleOnClose = () => {
    if (showElement) {
      showElement();
    }
    if (onClose) {
      onClose();
    }
  };

  return (
    <div
      className="image-gallery-container__navigation-bar"
      data-cy="image-gallery-container__navigation-bar"
      data-testid="image-gallery-container__navigation-bar"
    >
      <div
        className="image-gallery-container__navigation-bar__buttons"
        data-testid="image-gallery-container__navigation-bar__buttons"
        data-cy="image-gallery-container__navigation-bar__buttons"
      >
        <ButtonGroup
          buttons={buttons}
          activeButton={mainSelected === MainButtons.ImageGallery ? 0 : 1}
          buttonClassName={cn(
            isMobile &&
              'image-gallery-container__navigation-bar__buttons-mobile'
          )}
          activeButtonClassName={
            isMobile
              ? 'image-gallery-container__navigation-bar__buttons-mobile--active'
              : ''
          }
          onClick={(selected) => selectedClickHandle(selected)}
        />
        {currentSelection === MainButtons.View360 && !isMobile && (
          <>
            <div className="image-gallery-container__navigation-bar--divider"></div>
            <ButtonGroup
              buttons={subButtons}
              activeButton={
                subSelected && subSelected.indexOf('Exterior') > -1 ? 0 : 1
              }
              className="image-gallery-container__navigation-bar__buttons-subbuttons-container"
              onClick={(selected) => subSelectedClickHandle(selected)}
              buttonClassName="button-group__custom-button-sub"
              data-cy="image-gallery-container__navigation-bar__subbuttons"
              dataTestId="image-gallery-container__navigation-bar__subbuttons"
            />
          </>
        )}
      </div>
      <button
        type="button"
        data-cy="image-gallery-container__navigation-bar-close"
        data-testid="image-gallery-container__navigation-bar-close"
        className="image-gallery-container__navigation-bar-close"
        onClick={handleOnClose}
        aria-label="Close image gallery"
      >
        <CloseIcon fill={'var(--config-main-bg-colour)'} />
        {!isMobile && (
          <Typography size={'sm'} colour={'alternative'} variant={'button'}>
            Close Image Gallery
          </Typography>
        )}
      </button>
      {currentSelection === MainButtons.View360 && isMobile && renderMobile()}
    </div>
  );
};

export default ImageGalleryNavigationBar;
